import { dispatchAction } from '@ardoq/rxbeach';
import {
  type APIFieldAttributes,
  type APIViewpointAttributes,
  type FieldGlobalAttributes,
  type FilterInterfaceFilter,
  type FormattingFilter,
  isListFieldFormatting,
  isSystemFilter,
  type SystemFilterAttributes,
  SystemFilterType,
} from '@ardoq/api-types';
import { mapToPerspectiveEditorFormattingRule } from 'streams/perspectiveEditorData/mapToPerspectiveEditorFormattingRule';
import {
  type SetGroupingRulesPayload,
  setGroupingRules,
} from 'streams/grouping/GroupingActions';
import { isTruthy } from '@ardoq/common-helpers';
import {
  type PerspectivesFormatting,
  sortListFieldFormatting,
  isComponentOrReferenceFormattingRule,
} from '@ardoq/perspectives';
import { filterOperations } from '@ardoq/core';
import type { ExecuteViewpointPayload } from 'viewpointBuilder/actions';
import { partition } from 'lodash';
import type { FormattingRule } from '@ardoq/data-model';
import { resolveRuleFieldData } from 'perspectiveSidebar/perspectiveEditor/getFormattingForInvalidRuleByRuleId';
import {
  type ApplyFormattingAndLabelRulesParams,
  formattingAndLabelRulesRequested,
} from './actions';

export type { ApplyFormattingAndLabelRulesParams } from './actions';

export const applyViewpointPerspectives = (
  traversal:
    | Pick<APIViewpointAttributes, 'groupBys' | 'conditionalFormatting'>
    | ExecuteViewpointPayload
    | null
) => {
  if (!traversal) {
    return;
  }
  applyGroupingRules({ groupingRules: traversal.groupBys ?? [] });
  applyFormattingAndLabelRules({ rules: traversal.conditionalFormatting });
};

export const applyGroupingRules = (
  groupingRulesWithTransactionId: SetGroupingRulesPayload
) => {
  dispatchAction(setGroupingRules(groupingRulesWithTransactionId));
};

export const applyFormattingAndLabelRules = (
  params: ApplyFormattingAndLabelRulesParams
): void => {
  dispatchAction(formattingAndLabelRulesRequested(params));
};

export type FieldLookup = {
  globalFieldsByName: Record<string, FieldGlobalAttributes>;
  nonTemplateFieldsByName: Map<string, APIFieldAttributes>;
};

/**
 * Re-sorts the {@link ListFieldFormatting} value of a stored conditional
 * formatting rule so that keys appear in field-definition order.
 *
 * In a viewpoint, a formatting rule's `value` may contain
 * `ListFieldFormatting` keys in arbitrary insertion order. This function looks
 * up the canonical option order from the field definition and delegates to
 * {@link sortListFieldFormatting} to re-sort the keys.
 * Rules that are not list-field formatting rules are returned unchanged.
 */
const initializeFormattingRule = (
  rule: FormattingRule,
  { globalFieldsByName, nonTemplateFieldsByName }: FieldLookup
): FormattingRule => {
  if (!isComponentOrReferenceFormattingRule(rule)) {
    return rule;
  }
  if (!rule.field || !isListFieldFormatting(rule.value)) {
    return rule;
  }
  const fieldAttributes = resolveRuleFieldData(
    rule.field,
    globalFieldsByName,
    nonTemplateFieldsByName
  );
  if (!fieldAttributes) {
    return rule;
  }
  const listFieldOptions = (fieldAttributes.defaultValue as string)?.split(',');
  if (!listFieldOptions?.length) {
    return rule;
  }
  return {
    ...rule,
    value: sortListFieldFormatting(rule.value, listFieldOptions),
  };
};

export type FormattingAndLabelRulesApplication =
  | {
      isViewpointMode: true;
      labelFormatting: FilterInterfaceFilter[];
      conditionalFormattingRules: FormattingRule[];
      shouldGreyOut: boolean;
    }
  | {
      isViewpointMode: false;
      formatting: PerspectivesFormatting;
    };

export const toFormattingAndLabelRulesApplication = (
  rules: APIViewpointAttributes['conditionalFormatting'],
  isViewpointMode: boolean,
  fieldLookup: FieldLookup
): FormattingAndLabelRulesApplication => {
  const [systemFilters, formattingAndLabelRulesOnly] = partition(
    rules || [],
    isSystemFilter
  ) as [SystemFilterAttributes[], (FilterInterfaceFilter | FormattingFilter)[]];

  const [conditionalFormatting, labelFormatting] = partition(
    formattingAndLabelRulesOnly,
    filterOperations.isStrictlyFormattingFilter
  );

  const conditionalFormattingRules = conditionalFormatting
    .map(mapToPerspectiveEditorFormattingRule)
    .filter(isTruthy)
    .map(rule => initializeFormattingRule(rule, fieldLookup));

  if (isViewpointMode) {
    return {
      isViewpointMode,
      labelFormatting,
      conditionalFormattingRules,
      shouldGreyOut: systemFilters.some(
        setting => setting?.name === SystemFilterType.DEFAULT_COLOR
      ),
    };
  }

  const componentLabelFormat = labelFormatting.find(
    ({ affectComponent }) => affectComponent
  );
  const referenceLabelFormat = labelFormatting.find(
    ({ affectReference }) => affectReference
  );

  return {
    isViewpointMode,
    formatting: {
      conditionalFormattingRules,
      componentLabelFormattingValue: componentLabelFormat?.value,
      isComponentLabelTimeIncluded: componentLabelFormat?.includeTime,
      referenceLabelFormattingValue: referenceLabelFormat?.value,
      isReferenceLabelTimeIncluded: referenceLabelFormat?.includeTime,
    },
  };
};
